import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Filters,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  StatCard,
  StatCardList,
  Table,
  type FilterFieldSlot,
} from '@evogard/ui';
import { formatDate, formatInstant, loadFailureMessage } from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { useRealtimeConnected } from '../../lib/realtime-bus.js';
import { knownCodes, MirrorStamp, PaidValue, StatusBadge } from './common.js';

/** De quanto em quanto a lista se recarrega sozinha — ver "A lista acompanha a esteira". */
const REFRESH_MS = 15_000;
/**
 * O mesmo, com o tempo real de pé: quem relê é o aviso da projeção
 * (`live.ts`), e o relógio fica só de reserva para o aviso que se perder.
 */
const REFRESH_LIVE_MS = 120_000;

/**
 * A gestão de boletos do cadastro — `erp-39`.
 *
 * ## Os dois campos que não podem virar um
 *
 * `situacao` é o que o **fornecedor** chama aquele código, e `pago` é o que ele
 * responde sobre pagamento. A praça CE tem a situação **5 — BAIXADO C/ DESCONTO
 * com `pago = false`**: um boleto baixado que o ERP não considera pago.
 *
 * Uma coluna só obrigaria a tela a escolher entre chamá-lo de pago e de aberto,
 * e as duas são mentira. Então as duas aparecem, lado a lado, e quem opera
 * decide — que é exatamente o que a regra 14 pede.
 *
 * ## A soma é da seleção, e não da página
 *
 * A pergunta que esta tela existe para responder é "quanto tem nesta faixa". A
 * soma dos cinquenta da página responderia outra coisa com um número
 * convincente, e ninguém notaria.
 *
 * ## A lista acompanha a esteira
 *
 * Desde 13/10/2026 a ordem é a da última atualização vinda do ERP, a mais
 * recente primeiro, e a consulta se repete sozinha a cada `REFRESH_MS`. A
 * coluna "Atualizado do ERP" mostra o instante que ordena — ordenar pelo que a
 * tela não desenha deixaria a pessoa sem saber por que a linha subiu. O atraso
 * é o da esteira (minutos), e não o da recarga.
 */
export function BoletosScreen() {
  const { data: context } = useSessionContext();
  const live = useRealtimeConnected();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/boletos' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const applied = {
    situacao: search.situacao ?? '',
    pago: search.pago ?? '',
    dueFrom: search.dueFrom ?? '',
    dueUntil: search.dueUntil ?? '',
  };
  const { situacao, pago, dueFrom, dueUntil } = applied;
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: [
      'erp',
      'boletos',
      tenantId,
      term,
      situacao,
      pago,
      dueFrom,
      dueUntil,
      page,
      perPage,
    ],
    queryFn: async () => {
      const r = await api.erp.searchBoletos({
        body: {
          term: term.length >= 2 ? term : undefined,
          situacao: situacao ? [situacao] : undefined,
          /* omitir é "tanto faz", e nunca "os não pagos" */
          pago: pago === '' ? undefined : pago === 'sim',
          dueFrom: dueFrom || undefined,
          dueUntil: dueUntil || undefined,
          page,
          perPage,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os boletos'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
    /* a aba em segundo plano não recarrega: é o padrão do TanStack Query */
    refetchInterval: live ? REFRESH_LIVE_MS : REFRESH_MS,
  });

  const classifications = useQuery({
    queryKey: ['erp', 'classificacoes', tenantId],
    queryFn: async () => {
      const r = await api.erp.listClassifications({ query: {} });
      return r.status === 200 ? r.body : null;
    },
  });

  const codes = knownCodes(classifications.data, 'boleto');
  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);

  /*
   * O vencimento vem primeiro e ocupa as duas colunas, como a "Data de Criação"
   * da referência: é o recorte que muda o significado de todos os outros.
   *
   * "Situação" e "O ERP considera pago" continuam DOIS campos, pelo mesmo
   * motivo que são duas colunas na tabela — a CE tem boleto baixado que o ERP
   * não considera pago, e juntá-los obrigaria o filtro a escolher entre duas
   * mentiras. Ver o cabeçalho desta tela.
   */
  const fields: FilterFieldSlot[] = [
    { type: 'intervalo', label: 'Vencimento', from: 'dueFrom', to: 'dueUntil' },
    {
      type: 'selecao',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Todas',
      /* o NOME da situação, como o ERP a chama; o código só quando o nome falta */
      options: codes.map((c) => ({
        value: c.code,
        label: c.descricao ?? `Situação ${c.code}`,
      })),
    },
    {
      type: 'selecao',
      name: 'pago',
      label: 'O ERP considera pago',
      /* "Tanto faz" e não "Todos": omitir é não perguntar, e nunca "os não pagos" */
      placeholder: 'Tanto faz',
      options: [
        { value: 'sim', label: 'Sim' },
        { value: 'nao', label: 'Não' },
      ],
    },
  ];

  const filtered = Boolean(term || situacao || pago || dueFrom || dueUntil);

  return (
    <Page measure="cheia">
      <PageHeader title="Boletos">
        Os boletos de {context?.currentTenant.nome}, como o ERP os entrega. Os atualizados
        por último vêm primeiro, e a lista se recarrega sozinha.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {result?.estado === 'lida' && (
        <StatCardList>
          <StatCard
            icon="cobranca"
            label="Boletos na seleção"
            value={String(result.total)}
          />
          <StatCard
            icon="dinheiro"
            label="Valor somado"
            value={<MoneyValue value={result.totalValue} />}
          />
          <StatCard
            icon="confirmado"
            label="Valor pago"
            value={<MoneyValue value={result.totalPaid} />}
          />
        </StatCardList>
      )}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} boleto(s)`}
          descricao={<MirrorStamp at={result?.syncedAt ?? null} />}
          bleed
        >
          <Table
            paginate={false}
            /*
             * A busca fica FORA do painel, na barra: ela filtra enquanto se
             * digita, e é o único recorte cujo resultado a pessoa usa para
             * decidir o que digitar em seguida.
             */
            search={{
              label: 'Pesquisar boletos',
              placeholder: 'Código, nosso número, linha digitável, CPF/CNPJ ou nome',
              value: text,
              onChange: setText,
            }}
            toolbar={
              <Filters
                values={applied}
                fields={fields}
                onApply={(values) =>
                  list.setFilters({
                    situacao: String(values.situacao ?? '') || undefined,
                    pago: String(values.pago ?? '') || undefined,
                    dueFrom: String(values.dueFrom ?? '') || undefined,
                    dueUntil: String(values.dueUntil ?? '') || undefined,
                  })
                }
              />
            }
            label="Boletos do cadastro"
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            rowKey={(b) => b.code}
            /* a seta é montada aqui: `@evogard/ui` não conhece o roteador */
            rowLink={(b) => (
              <Link
                to="/erp/boletos/$code"
                params={{ code: b.code }}
                aria-label={`Abrir o boleto ${b.code}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            empty={
              filtered
                ? 'Nenhum boleto com esses filtros.'
                : 'O cadastro de boletos desta base está vazio.'
            }
            columns={[
              { title: 'Código', minWidth: '6rem', cell: (b) => b.code },
              {
                /* o instante que ORDENA a lista — ver o cabeçalho da tela */
                title: 'Atualizado do ERP',
                minWidth: '10rem',
                cell: (b) =>
                  b.syncedAt ? (
                    <span className="tabular-nums">{formatInstant(b.syncedAt)}</span>
                  ) : (
                    <NotProvided>não veio do ERP</NotProvided>
                  ),
              },
              {
                title: 'Associado',
                minWidth: '14rem',
                cell: (b) => b.member?.nome ?? <NotProvided>sem associado</NotProvided>,
              },
              {
                title: 'Vencimento',
                minWidth: '8rem',
                cell: (b) => (b.dueDate ? formatDate(b.dueDate) : <NotProvided />),
              },
              {
                title: 'Valor',
                alignment: 'fim',
                minWidth: '7rem',
                cell: (b) => <MoneyValue value={b.value} />,
              },
              {
                title: 'Pago',
                alignment: 'fim',
                minWidth: '9rem',
                cell: (b) => <PaidValue value={b.valuePaid} source={b.paymentSource} />,
              },
              {
                title: 'Competência',
                minWidth: '7rem',
                cell: (b) => b.period ?? <NotProvided />,
              },
              {
                /*
                 * O número impresso no papel. Entra porque é o que o associado
                 * lê ao telefone — quem atende precisa confirmar que está
                 * olhando o mesmo boleto. É dado, e nunca endereço: a regra 7
                 * reserva a identidade para o `code`.
                 */
                title: 'Nosso número',
                minWidth: '9rem',
                cell: (b) =>
                  b.nossoNumero ? (
                    <span className="font-mono text-xs">{b.nossoNumero}</span>
                  ) : (
                    <NotProvided />
                  ),
              },
              {
                title: 'Situação',
                minWidth: '12rem',
                cell: (b) => <StatusBadge situacao={b.situacao} />,
              },
              {
                title: 'O ERP considera pago',
                minWidth: '9rem',
                cell: (b) =>
                  b.pago === null ? (
                    <NotProvided>o ERP não disse</NotProvided>
                  ) : (
                    <Badge tone={b.pago ? 'sucesso' : 'neutro'}>
                      {b.pago ? 'sim' : 'não'}
                    </Badge>
                  ),
              },
            ]}
          />

          {result?.estado === 'lida' && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="boletos"
              /* o cartão é `bleed`; o espaçamento é de quem entra nele */
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
